import type { CreateImportOrderDTO } from "@/modules/inventory/domain/import-order-contracts";
import type { ImportOrderPhase13LineDTO } from "@/modules/inventory/domain/types";
import { normalizeStockUnitLabel } from "@/modules/products/domain/unit-conversion";

export type ImportOrderPhase13MatchedLineForm = {
  kind: "matched";
  lineKey: string;
  variantId: string;
  productName: string;
  variantName: string;
  imageKey: string | null;
  description: string;
  orderedQuantity: number;
  unitPriceForeign: number;
  stockUnitLabel: string;
  receiveUnitLabel: string | null;
  receiveConversionFactor: number;
  minOrderedQuantity?: number;
  priceFromHistory?: boolean;
};

export type ImportOrderPhase13PendingLineForm = {
  kind: "pending";
  lineKey: string;
  externalTitle: string;
  externalUrl: string;
  orderedQuantity: number;
  unitPriceForeign: number;
};

/** @deprecated legacy rows without kind — treated as matched */
export type ImportOrderPhase13LegacyLineForm = Omit<
  ImportOrderPhase13MatchedLineForm,
  "kind" | "lineKey"
> & {
  kind?: undefined;
  lineKey?: string;
  variantId: string;
};

export type ImportOrderPhase13LineForm =
  | ImportOrderPhase13MatchedLineForm
  | ImportOrderPhase13PendingLineForm
  | ImportOrderPhase13LegacyLineForm;

export function isPendingImportLineForm(
  line: ImportOrderPhase13LineForm,
): line is ImportOrderPhase13PendingLineForm {
  return line.kind === "pending";
}

export function isMatchedImportLineForm(
  line: ImportOrderPhase13LineForm,
): line is ImportOrderPhase13MatchedLineForm {
  return line.kind === "matched" || (!line.kind && "variantId" in line);
}

export function newPendingImportLineForm(): ImportOrderPhase13PendingLineForm {
  return {
    kind: "pending",
    lineKey: `pending-${crypto.randomUUID()}`,
    externalTitle: "",
    externalUrl: "",
    orderedQuantity: 1,
    unitPriceForeign: 0,
  };
}

export function normalizeImportLineForm(
  line: ImportOrderPhase13LineForm,
): ImportOrderPhase13MatchedLineForm | ImportOrderPhase13PendingLineForm {
  if (isPendingImportLineForm(line)) {
    return {
      ...line,
      lineKey: line.lineKey || `pending-${crypto.randomUUID()}`,
      externalUrl: line.externalUrl ?? "",
    };
  }

  const matched = isMatchedImportLineForm(line) ? line : null;
  if (!matched) {
    throw new Error("Invalid import line form");
  }

  return {
    kind: "matched",
    lineKey: matched.lineKey || matched.variantId,
    variantId: matched.variantId,
    productName: matched.productName,
    variantName: matched.variantName,
    imageKey: matched.imageKey ?? null,
    description: matched.description ?? "",
    orderedQuantity: matched.orderedQuantity,
    unitPriceForeign: matched.unitPriceForeign,
    stockUnitLabel: normalizeStockUnitLabel(matched.stockUnitLabel),
    receiveUnitLabel: matched.receiveUnitLabel?.trim() || null,
    receiveConversionFactor: Number(matched.receiveConversionFactor) || 1,
    minOrderedQuantity: matched.minOrderedQuantity,
    priceFromHistory: matched.priceFromHistory,
  };
}

export function importLineDtoToFormLine(
  line: ImportOrderPhase13LineDTO,
): ImportOrderPhase13MatchedLineForm | ImportOrderPhase13PendingLineForm {
  if (line.matchStatus === "pending" || line.variantId == null) {
    return {
      kind: "pending",
      lineKey: line.id,
      externalTitle: line.externalTitle?.trim() || line.productName,
      externalUrl: line.externalUrl ?? "",
      orderedQuantity: line.orderedQuantity,
      unitPriceForeign: Number(line.unitPriceForeign),
    };
  }

  return {
    kind: "matched",
    lineKey: line.id,
    variantId: line.variantId,
    productName: line.productName,
    variantName: line.variantName,
    imageKey: line.externalImageKey,
    description: line.description ?? "",
    orderedQuantity: line.orderedQuantity,
    minOrderedQuantity: Math.max(1, line.receivedQuantity + line.shortQuantity),
    unitPriceForeign: Number(line.unitPriceForeign),
    stockUnitLabel: line.stockUnitLabel,
    receiveUnitLabel: line.receiveUnitLabel,
    receiveConversionFactor: line.receiveConversionFactor,
  };
}

export function importLineFormToApiInput(
  line: ImportOrderPhase13MatchedLineForm | ImportOrderPhase13PendingLineForm,
): CreateImportOrderDTO["lines"][number] {
  if (line.kind === "pending") {
    return {
      kind: "pending",
      externalTitle: line.externalTitle.trim(),
      externalUrl: line.externalUrl.trim() || null,
      orderedQuantity: line.orderedQuantity,
      unitPriceForeign: line.unitPriceForeign,
    };
  }

  return {
    kind: "matched",
    variantId: line.variantId,
    description: line.description.trim() || null,
    orderedQuantity: line.orderedQuantity,
    unitPriceForeign: line.unitPriceForeign,
  };
}

export function lineFormIsValid(line: ImportOrderPhase13LineForm): boolean {
  const normalized = normalizeImportLineForm(line);
  if (normalized.kind === "pending") {
    return (
      normalized.externalTitle.trim().length > 0 &&
      normalized.orderedQuantity > 0 &&
      normalized.unitPriceForeign >= 0
    );
  }
  return (
    normalized.variantId.length > 0 &&
    normalized.orderedQuantity > 0 &&
    normalized.unitPriceForeign >= 0
  );
}
